import type { LocalDate } from "@js-joda/core";
import { Api } from "services/api/api.service";
import type {
  AddStaffWorkerTaxRequest,
  StaffProfileData,
} from "services/api/company/staff.service";
import {
  toIsoDateStartOfDayUtc,
  toNonNegativeNumber,
} from "./domains/employment/profilePanel.payload.logic";
import type { EmploymentDetailsForm } from "./profilePanel.contract";

interface AddTaxParams {
  companyId: number | null;
  workerId: number | null;
  profile: StaffProfileData | null;
  currentEmploymentStartDate: LocalDate | null;
  effectiveFromDate: LocalDate | null;
  editedEmploymentDetails: EmploymentDetailsForm;
  translate: (key: string, options?: Record<string, unknown>) => string;
  setSaveError: (value: string) => void;
  executeSavingAction: (action: () => Promise<void>) => void;
  reloadProfile: () => void;
}

interface DeleteTaxParams {
  companyId: number | null;
  workerId: number | null;
  taxId: number;
  executeSavingAction: (action: () => Promise<void>) => void;
  reloadProfile: () => void;
}

/**
 * Adds a new tax entry for a worker based on the current form state.
 */
export const addTax = (params: AddTaxParams): void => {
  if (params.companyId == null || params.workerId == null) return;
  const companyId = params.companyId;
  const workerId = params.workerId;

  const startDate = params.effectiveFromDate;
  if (startDate == null) {
    params.setSaveError(params.translate("Alerts.SomethingWentWrong"));
    return;
  }
  if (
    params.currentEmploymentStartDate != null &&
    startDate.isBefore(params.currentEmploymentStartDate)
  ) {
    params.setSaveError(
      params.translate("Staff.ProfilePanelTaxDateBeforeEmploymentStart"),
    );
    return;
  }
  if (startDate.dayOfMonth() !== 1) {
    params.setSaveError(
      params.translate("Staff.ProfilePanelTaxDateMustBeFirstOfMonth"),
    );
    return;
  }

  const activeTax =
    params.profile?.taxes.find((tax) => tax.isActive) ??
    params.profile?.taxes[0] ??
    null;

  const payload: AddStaffWorkerTaxRequest = {
    startDate: toIsoDateStartOfDayUtc(startDate),
    taxType: activeTax?.type ?? "Undefined",
    value: toNonNegativeNumber(params.editedEmploymentDetails.taxTable),
  };

  params.executeSavingAction(async () => {
    await Api().company.staff.addWorkerTax(companyId, workerId, payload);
    params.reloadProfile();
  });
};

/**
 * Deletes a tax entry for a worker and refreshes profile data.
 */
export const deleteTax = (params: DeleteTaxParams): void => {
  if (params.companyId == null || params.workerId == null) return;
  const companyId = params.companyId;
  const workerId = params.workerId;

  params.executeSavingAction(async () => {
    await Api().company.staff.deleteWorkerTax(
      companyId,
      workerId,
      params.taxId,
    );
    params.reloadProfile();
  });
};
